'use client';

import LoadGoogleMapWithShimmer from '@/hotdesk-ui/components/loading/GoogleMapWithShimmer';
import { FC, Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMapsLoaded } from '@/hotdesk-ui/helpers/google-maps-loader';
import { GOOGLE_MAP_DARK_STYLE } from '@/hotdesk-ui/services/google-map.service';
import MapContainer from './MapContainer';
import styles from './MapContainer.module.scss';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { clusterListingsForMap, MapBounds, MapCluster } from '@/hotdesk-ui/helpers/location.helper';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { isAreaWideGeo } from '@/hotdesk-ui/helpers/explore-location-params';
import { RootState } from '@/hotdesk-ui/store/store';
export interface MapContainerTileProps {
  userListing?: MyListing[];
  userWorkspace?: ReadOnlyListing[];
  lat?: number;
  lng?: number;
  /**
   * Box around the current search's pins. When given, the map opens (and
   * re-opens on every new search) fitted to it instead of centred on the
   * `lat`/`lng` centroid — a country's centroid is usually empty land.
   */
  fitBounds?: MapBounds | null;
  activeListing?: number | null;
  onClickExpandMap?: (data: boolean) => void;
  expandMap?: boolean;
  type?: string;
  transformArray?: object[];
  drawerStatus?: string;
  onCanvasChanged?: (center: {
    lat: number;
    lng: number;
  }, radiusKm: number, zoom: number) => void;
}
const containerStyle = {
  width: '100%',
  height: '100%'
};

/**
 * A coordinate the map can actually be centred on. Rules out NaN (the
 * centroid of an empty result set) and the 0 default, which is Null Island
 * rather than a real place.
 */
const isUsableCoord = (value?: number): value is number => typeof value === 'number' && Number.isFinite(value) && value !== 0;

/** Zoom ceiling for a bounds fit, so a single pin doesn't land at street level. */
const MAX_FIT_ZOOM = 14;
/** Keeps fitted pins clear of the map edges (and the zoom controls). */
const FIT_PADDING_PX = 56;
const boundsKeyOf = (bounds?: MapBounds | null): string | null => bounds ? `${bounds.north}|${bounds.south}|${bounds.east}|${bounds.west}` : null;
const MapContainerTile: FC<MapContainerTileProps> = ({
  userListing = [],
  userWorkspace = [],
  lat = 0,
  lng = 0,
  fitBounds = null,
  activeListing = null,
  onClickExpandMap,
  expandMap = false,
  type,
  transformArray,
  drawerStatus,
  onCanvasChanged
}) => {
  const [zoom, setZoom] = useState(12);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const geoLocation = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);

  // The results centroid is NaN while a search is in flight (it averages an
  // empty list) and 0,0 when there is nothing to average at all. Handing
  // either to `map.setCenter` leaves Google Maps blank, so fall back to the
  // location the user actually searched for.
  const centroidUsable = isUsableCoord(lat) && isUsableCoord(lng);
  const seedLat = centroidUsable ? lat : geoLocation?.lat;
  const seedLng = centroidUsable ? lng : geoLocation?.log;
  const seedUsable = isUsableCoord(seedLat) && isUsableCoord(seedLng);
  const [center, setCenter] = useState(() => seedUsable ? {
    lat: seedLat as number,
    lng: seedLng as number
  } : {
    lat: 0,
    lng: 0
  });
  // True once a real centre has been applied — after that the map is
  // user-controlled: panning must not re-centre it (the results centroid
  // drifts as cards accumulate), so deliberate recentres go through the
  // search-driven effect below.
  const hasRealCenterRef = useRef(seedUsable);
  useEffect(() => {
    if (!seedUsable) return;
    const next = {
      lat: seedLat as number,
      lng: seedLng as number
    };
    if (!mapRef) {
      hasRealCenterRef.current = true;
      setCenter(next);
      return;
    }
    // The map is already up. Only step in if it was mounted before any
    // real centre was known — e.g. it came up during a search, when the
    // centroid was still NaN — otherwise this would yank the user's view.
    if (hasRealCenterRef.current) return;
    hasRealCenterRef.current = true;
    mapRef.setCenter(next);
  }, [seedLat, seedLng, seedUsable, mapRef]);

  // Fitting the pins is what actually frames a search: centring on their
  // average at a city-level zoom drops the map between the clusters, which
  // for a country search ("United States") is open country with nothing on
  // it. `pendingFit` marks a search whose pins haven't been framed yet — set
  // on mount and on every new search, cleared once a fit is applied.
  const pendingFitRef = useRef(true);
  const appliedFitKeyRef = useRef<string | null>(null);
  const fitBoundsRef = useRef(fitBounds);
  fitBoundsRef.current = fitBounds;
  const applyFit = useCallback((map: google.maps.Map, bounds: MapBounds) => {
    appliedFitKeyRef.current = boundsKeyOf(bounds);
    pendingFitRef.current = false;
    hasRealCenterRef.current = true;

    // A single pin (or several at one address) has no extent to fit —
    // fitBounds would zoom to the maximum, so just centre on it.
    if (bounds.north === bounds.south && bounds.east === bounds.west) {
      map.setCenter({
        lat: bounds.north,
        lng: bounds.east
      });
      return;
    }
    map.fitBounds(bounds, FIT_PADDING_PX);
    // fitBounds settles asynchronously, so read the zoom it picked once the
    // map is idle — both to cap it and to keep clustering in sync.
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const z = map.getZoom();
      if (typeof z !== 'number') return;
      if (z > MAX_FIT_ZOOM) {
        map.setZoom(MAX_FIT_ZOOM);
        setZoom(MAX_FIT_ZOOM);
        return;
      }
      setZoom(z);
    });
  }, []);

  // Recenter only on an actual new search. Map-pan refetches never call
  // setGeoLocation, so geoLocation is stable while dragging — it changes only
  // when the user picks a place / uses Nearby. Pan imperatively so the map
  // stays mounted and the user keeps dragging freely; only the cards reload.
  // Runs before the fit effect below so a search whose results land in the
  // same commit is still framed.
  const lastGeoKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = geoLocation ? `${geoLocation.lat}|${geoLocation.log}|${geoLocation.formatted_address}` : null;
    const prev = lastGeoKeyRef.current;
    lastGeoKeyRef.current = key;
    if (!key || prev === null || prev === key) return;
    // New search: refit to its pins once they arrive.
    pendingFitRef.current = true;
    if (!mapRef) return;
    // A country (or state) has no meaningful point centre — panning there
    // would show the empty middle of it, so leave the view to the fit below.
    if (isAreaWideGeo(geoLocation)) return;
    if (geoLocation?.lat && geoLocation?.log) {
      mapRef.panTo({
        lat: geoLocation.lat,
        lng: geoLocation.log
      });
    }
  }, [geoLocation, mapRef]);

  // Frame the pins as soon as the search that produced them lands. Guarded by
  // the bounds key so the stale results still on screen when a new search
  // starts don't consume the pending fit, and so a pan-driven refetch (which
  // leaves pendingFit false) never yanks the view the user just dragged to.
  useEffect(() => {
    if (!mapRef || !fitBounds || !pendingFitRef.current) return;
    if (boundsKeyOf(fitBounds) === appliedFitKeyRef.current) return;
    applyFit(mapRef, fitBounds);
  }, [mapRef, fitBounds, applyFit]);
  // The map is uncontrolled — `center`/`zoom` aren't passed as props (which
  // would snap the view back after every imperative panTo/setZoom). We seed
  // the initial view once in onLoad, reading the latest values via refs so a
  // stale closure can't seed 0,0.
  const centerRef = useRef(center);
  centerRef.current = center;
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const zoomIn = () => {
    const newZoom = zoom + 1;
    setZoom(newZoom);
    mapRef?.setZoom(newZoom);
  };
  const zoomOut = () => {
    const newZoom = zoom - 1;
    setZoom(newZoom);
    mapRef?.setZoom(newZoom);
  };
  const listings = type == 'workspace' ? userWorkspace : userListing;

  // Sync the zoom state with the map's actual zoom — the +/- buttons already
  // do this, but pinch/double-click/wheel zoom also need to drive a recluster.
  const handleZoomChanged = useCallback(() => {
    const z = mapRef?.getZoom();
    if (typeof z === 'number') setZoom(z);
  }, [mapRef]);

  // Fire onCanvasChanged once after the user stops panning/zooming. We
  // debounce by 500ms so rapid intermediate idle events (caused by the
  // <GoogleMap key={center}> remount-on-pan dance) collapse into a single
  // fetch. The very first idle right after onLoad is skipped so we don't
  // re-fetch SSR-seeded data.
  const skipFirstIdle = useRef(true);
  // Set when a popup pans the map to fit its card — that's not the user
  // moving the map, so its idle must not refetch (and swap the pins out
  // from under the open card).
  const skipAutoPanIdle = useRef(false);
  const handleAutoPan = useCallback(() => {
    skipAutoPanIdle.current = true;
  }, []);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!mapRef || !onCanvasChanged) return;
    const listener = mapRef.addListener('idle', () => {
      if (skipFirstIdle.current) {
        skipFirstIdle.current = false;
        return;
      }
      if (skipAutoPanIdle.current) {
        skipAutoPanIdle.current = false;
        return;
      }
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        const bounds = mapRef.getBounds();
        const c = mapRef.getCenter();
        const z = mapRef.getZoom();
        if (!bounds || !c || typeof z !== 'number') return;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const meanLat = (ne.lat() + sw.lat()) / 2;
        const cosLat = Math.cos(meanLat * Math.PI / 180);
        const R = 6371; // km
        const halfHeightKm = (ne.lat() - sw.lat()) / 2 * Math.PI / 180 * R;
        const halfWidthKm = (ne.lng() - sw.lng()) / 2 * Math.PI / 180 * R * cosLat;
        const radiusKm = Math.max(halfHeightKm, halfWidthKm);
        onCanvasChanged({
          lat: c.lat(),
          lng: c.lng()
        }, radiusKm, z);
      }, 500);
    });
    return () => {
      listener.remove();
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    };
  }, [mapRef, onCanvasChanged]);
  const renderClusters = useMemo<MapCluster[]>(() => clusterListingsForMap(listings as unknown as MyListing[], zoom, type), [listings, zoom, type]);
  const [activeMarker, setActiveMarker] = useState<number | null>(null);
  const [hoverListing, setHoverListing] = useState<number | null>(activeListing);
  useEffect(() => {
    setHoverListing(activeListing);
  }, [activeListing]);
  const handleMapClick = () => {
    setActiveMarker(null);
  };
  const isLoaded = useGoogleMapsLoaded();
  const t = useTranslations();
  const mapOptions = {
    mapTypeControl: false,
    zoomControl: false,
    fullscreenControl: false,
    cameraControl: false,
    streetViewControl: false,
    myLocationButton: false,
    scaleControl: false,
    rotateControl: false,
    gestureHandling: "greedy",
    styles: GOOGLE_MAP_DARK_STYLE
  };
  return <Fragment>
        
            <div className={styles['container-style']}>
                {isLoaded && <Fragment>
                        <div className={styles['zoom-control-style']}>
                            <button onClick={zoomIn} className={styles['zoom-in']}>
                                ＋
                            </button>
                            <button onClick={zoomOut} className={styles['zoom-out']}>
                                −
                            </button>
                        </div>
                        <GoogleMap mapContainerStyle={containerStyle} onClick={() => handleMapClick()} onLoad={map => {
          map.setCenter(centerRef.current);
          map.setZoom(zoomRef.current);
          // Frame the pins here rather than letting the
          // effect below do it a tick later, so the map
          // never flashes the centroid view first.
          if (fitBoundsRef.current && pendingFitRef.current) {
            applyFit(map, fitBoundsRef.current);
          }
          setMapRef(map);
        }} onZoomChanged={handleZoomChanged} options={mapOptions}>
                            {renderClusters.map((cluster, index) => <div className="row px-md-3 py-2" key={`cluster_${cluster.rep.id ?? index}`}>
                                    <MapContainer listing={cluster.rep} selectedList={activeMarker} selectList={(markerId: number | null) => {
              setActiveMarker(markerId);
            }} hoverListing={hoverListing} mapRef={mapRef} type={type} transformArray={transformArray} siblings={cluster.members} clusterCount={cluster.members.length} onAutoPan={handleAutoPan} />
                                </div>)}
                        </GoogleMap>
                    </Fragment>}
                {!isLoaded && <>
                        <LoadGoogleMapWithShimmer />
                    </>}
            </div>
        </Fragment>;
};
export default memo(MapContainerTile);